import { action, computed, observable } from 'mobx'
import ModelStore from '../../../shared-lib/stores/ModelStore'
import { CampaignMessageType, CampaignStatus, CampaignType, EmailContentBlockType } from '../../../graphQL/generated/enums'
import { recipientText, sentCountText, automatedCampaignDefaults, scheduledCampaignDefaults } from '../../lib/campaignHelpers'
import { $RootStore } from '../../../src/stores/RootStore'

export const defaultThemeColors = {
  themePrimary: '#FFFFFF',
  themeBackground: '#FFFFFF',
  themeTextColor: '#46424A',
  contrastTextColor: '#FFFFFF',
}

export const defaultEmailCampaignData = {
  _id: '',
  label: '',
  type: CampaignType.Broadcast,
  messageType: CampaignMessageType.Email,
  emailSubject: '',
  emailContent: [],
  scheduledFor: undefined,
  applyToAllClients: false,
  customRecipientIds: [],
  recipientClientGroups: [],
  themeColors: { ...defaultThemeColors },
  status: CampaignStatus.Draft,
  createdAt: new Date(),
  sentCount: 0,
}

export default class EmailCampaignModel extends ModelStore {
  @observable accessor data: EmailCampaign = { ...defaultEmailCampaignData }
  fetchMethodName = 'emailCampaign'
  saveMethodName = 'updateEmailCampaign'
  @observable accessor automatedCampaignData: AutomatedCampaign = { ...automatedCampaignDefaults }
  @observable accessor scheduledCampaignData: ScheduledCampaign = { ...scheduledCampaignDefaults }
  @observable accessor imagesToDelete: string[] = []

  constructor(data: Partial<EmailCampaign>, rootStore: $RootStore) {
    super(data, rootStore)
    this.merge(data)
  }

  @computed get recipientText() {
    return recipientText(this.data, this.rootStore.i18n)
  }

  @computed get sentCountText() {
    return sentCountText(this.data, this.rootStore.i18n)
  }

  getScheduledCampaign = async () => {
    const { communication: { requester } } = this.rootStore
    this.setLoading(true)

    const { scheduledCampaign } = await requester.scheduledCampaign({ campaignId: this.data._id })

    this.setLoading(false)

    this.setScheduledCampaignData(scheduledCampaign
      ? scheduledCampaign
      : {
        ...scheduledCampaignDefaults,
        campaignId: this.data._id,
        messageType: CampaignMessageType.Email,
      },
    )
  }

  getAutomatedCampaign = async () => {
    const { communication: { requester } } = this.rootStore

    const defaultData = { ...automatedCampaignDefaults, campaignId: this.data._id }

    if (this.data._id === '') {
      this.setAutomatedCampaignData(defaultData)
      return
    }

    this.setLoading(true)
    const { automatedCampaign } = await requester.automatedCampaign({ campaignId: this.data._id })
    this.setLoading(false)

    this.setAutomatedCampaignData(automatedCampaign ? automatedCampaign : defaultData)
  }

  updateAutomatedCampaign = async (automatedCampaignData: Partial<AutomatedCampaign> = {}) => {
    const automatedCampaign = {
      ...this.automatedCampaignData,
      ...automatedCampaignData,
      campaignId: this.data._id,
      active: this.data.status === CampaignStatus.Active,
      sendLog: undefined,
      messageType: CampaignMessageType.Email,
    }

    this.setLoading(true)

    const { updateAutomatedCampaign } = await this.rootStore.communication.requester
      .updateAutomatedCampaign({ data: automatedCampaign })

    this.setAutomatedCampaignData({
      ...this.automatedCampaignData,
      ...updateAutomatedCampaign,
    })

    if (automatedCampaign.campaignId) {
      const campaign = this.rootStore.emailCampaignStore.findItemById(automatedCampaign.campaignId)
      if (campaign) {
        campaign.automatedCampaignData = this.automatedCampaignData
      }
    }

    this.setLoading(false)
  }

  updateScheduledCampaign = async (scheduledCampaignData: Partial<ScheduledCampaign> = {}) => {
    const scheduledCampaign = {
      ...this.scheduledCampaignData,
      ...scheduledCampaignData,
      campaignId: this.data._id,
      timeToSend: undefined,
      messageType: CampaignMessageType.Email,
    }

    this.setLoading(true)

    const { updateScheduledCampaign } = await this.rootStore.communication.requester
      .updateScheduledCampaign({ data: scheduledCampaign })

    this.setScheduledCampaignData({
      ...this.scheduledCampaignData,
      ...updateScheduledCampaign,
    })

    this.setLoading(false)
  }

  sendTest = async (emailAddress: string, externalBookingId?: string) => {
    const { emailSubject, emailContent, themeColors } = this.data

    const data = {
      emailSubject,
      emailContent,
      themeColors,
      sendTo: emailAddress,
      triggerType: this.data.triggerType,
      externalBookingId,
    }

    this.rootStore.communication.requester.sendTestEmail({ data })
  }

  @computed get previewText() {
    const text = this.data.emailContent
      .filter(content => (
        content.blockType === EmailContentBlockType.Text || content.blockType === EmailContentBlockType.Title
      ))
      .map(context => context.text)
      .join(' ')
      .slice(0, 100)

    return text.length > 0 ? `${text}...` : 'No content'
  }

  @computed get isLoyalty() {
    return this.automatedCampaignData.triggerType.includes('loyalty')
  }

  @computed get isClientReferral() {
    return this.automatedCampaignData.triggerType.includes('clientReferral')
  }

  @computed get isTransactional() {
    return this.data.triggerType?.includes('tr_')
  }

  @computed get isDefault() {
    return !(this.isLoyalty || this.isClientReferral || this.isTransactional)
  }

  @action setLoading = (loading: boolean) => {
    this.loading = loading
  }

  @action setAutomatedCampaignData = (automatedCampaignData: AutomatedCampaign) => {
    this.automatedCampaignData = automatedCampaignData
  }

  @action setScheduledCampaignData = (scheduledCampaignData: ScheduledCampaign) => {
    this.scheduledCampaignData = scheduledCampaignData
  }
}
